import { gqlFetcher, graphql } from '#features/api';
import { FilterInput, OrderInput } from '#features/api/types';
import { isDefined } from '#utils/isDefined';

const notificationsDoc = graphql(`
  query Notifications($offset: Int, $limit: Int, $orders: [OrderInput], $filters: [FilterInput]) {
    notification {
      messages(offset: $offset, limit: $limit, orders: $orders, filters: $filters) {
        notifications {
          id
          userData {
            displayName
            userId
            avatar
          }
          message
          contentType
          type
          createdAt
          updatedAt
          count
          isRead
        }
        hasMore
        totalCount
      }
    }
  }
`);

export const notificationsQueryOptions = (input: {
  offset: number;
  limit: number;
  orders?: OrderInput[];
  filters?: FilterInput[];
}) => ({
  queryKey: ['notifications', input],
  queryFn: () =>
    gqlFetcher(notificationsDoc, input).then(
      (data) => data?.notification?.messages?.notifications?.filter(isDefined) ?? []
    ),
});

const hasUnreadNotificationsDoc = graphql(`
  query HasUnreadNotifications {
    notification {
      exist
    }
  }
`);

export const hasUnreadNotificationsQueryOptions = () => ({
  queryKey: ['hasUnreadNotifications'],
  queryFn: () =>
    gqlFetcher(hasUnreadNotificationsDoc).then((data) => Boolean(data?.notification?.exist)),
});

const readAllNotificationsDoc = graphql(`
  mutation ReadAllNotifications {
    notification {
      readAll {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const readAllNotifications = () =>
  gqlFetcher(readAllNotificationsDoc).then((data) => data?.notification?.readAll);

const readChatNotificationsDoc = graphql(`
  mutation ReadChatNotifications($parentId: UUID!) {
    notification {
      readChat(parentId: $parentId) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const readChatNotifications = (input: { parentId: string }) =>
  gqlFetcher(readChatNotificationsDoc, input).then((data) => data?.notification?.readChat);

const deleteAllNotificationsDoc = graphql(`
  mutation DeleteAllNotifications {
    notification {
      removeAll {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const deleteAllNotifications = () =>
  gqlFetcher(deleteAllNotificationsDoc).then((data) => data?.notification?.removeAll);

const addPushTokenDoc = graphql(`
  mutation AddPushToken($input: PushTokenInput!) {
    push {
      addToken(input: $input) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const addPushToken = (input: {
  token: string;
  platform?: string;
  deviceOem?: string;
  callScreenReady?: boolean;
  appVersion?: number;
  // Снимок состояния телефона: едет попутно с регистрацией и ложится в строку токена.
  sdkInt?: number;
  batteryExempt?: boolean;
  bgRestricted?: boolean;
  standbyBucket?: number;
  notificationsEnabled?: boolean;
  fsiAllowed?: boolean;
}) =>
  gqlFetcher(addPushTokenDoc, { input }).then((data) => data?.push?.addToken);

const removePushTokenDoc = graphql(`
  mutation RemovePushToken($input: PushTokenInput!) {
    push {
      removeToken(input: $input) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const removePushToken = (input: { token: string }) =>
  gqlFetcher(removePushTokenDoc, { input }).then((data) => data?.push?.removeToken);
